import { Sparkles } from 'lucide-react';
import { useToast } from '@/components/ux/Toast';
import { useEnqueueTriageGlobalRun } from '../../api/useAdminTriage';

// "(Re)run learning": enqueues a global triage run (docs/TRIAGE.md §5).
// - reflect: reviews the last 30 days of draft edits, rejections and replies
//   humans sent (including ones typed in Gmail).
// - bootstrap: reads the mailbox's sent history to seed voice, answers and
//   exemplars.
// Both file playbook proposals; nothing changes until a human approves them
// on the Playbook page.
const LABELS = {
  'triage.reflect': {
    idle: 'Run learning',
    title: 'Review recent draft edits, rejections and sent replies and propose playbook updates',
  },
  'triage.bootstrap': {
    idle: 'Learn from sent mail',
    title: "Read the mailbox's sent history and propose voice, answers and exemplar replies",
  },
} as const;

export default function RunLearningButton({
  className = '',
  taskType = 'triage.reflect',
}: {
  className?: string;
  taskType?: keyof typeof LABELS;
}) {
  const toast = useToast();
  const enqueue = useEnqueueTriageGlobalRun();

  const run = () => {
    enqueue.mutate(
      { taskType },
      {
        onSuccess: (res) => {
          if (res.data?.outcome === 'existing') {
            toast.success('A learning run is already active; it will re-run with the latest data');
          } else {
            toast.success('Learning run queued. Proposals will show up on the Playbook page.');
          }
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Could not queue the learning run'),
      }
    );
  };

  return (
    <button
      onClick={run}
      disabled={enqueue.isPending}
      title={LABELS[taskType].title}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium text-dark-muted hover:border-primary-400 hover:text-primary-500 disabled:opacity-50 ${className}`}
    >
      <Sparkles className={`w-3.5 h-3.5 ${enqueue.isPending ? 'animate-pulse' : ''}`} />
      {enqueue.isPending ? 'Queuing…' : LABELS[taskType].idle}
    </button>
  );
}
